import { isEmpty } from 'lodash';
import { convertSearchObjsToQueryString } from './Helpers/filterQuery-helpers';

/**
 * This class will aid in tracking the various device selections including the devices not yet downloaded
 * in the frontend. Currently, when we do a select all, only the devices which are present in the frontend are
 * selected. We need to support all the devices assoited with the filter,group, account , query
 * Typical End user usecases:
 * - User wants to select all the devices as per group,query,filter,account
 * - User wants to select all devices as above but exclude a few devices
 *
 * Based on the above, we will have only two properties
 * selectionParams
 * - "undefined" means the user has not selected the device in context
 * - any value including an empty array  means that user has made a contextual selection
 * excludedDevices:
 *  - undefined if selection params undefined means not set state
 *  - undefined if selection params not undefined means user has not excluded any devices
 *  - empty array means user has not excluded any serials
 *  - non empty array means user has excluded the serials
 *
 */
class DeviceSelectionModel {
  selectionParams: any[] | undefined // This has the search params array as per filter
  excludedDevices: string[] | undefined // this will be a list of devices downloaded in frontend which are selected

  constructor(selectionParams: any) {
    if (typeof selectionParams === 'undefined') return

    // if selection params are not an array, e will just return
    if (!Array.isArray(selectionParams)) return

    this.selectionParams = [...selectionParams];
    this.excludedDevices = [];
  }
  addToExcludedDevices(serial: string) {
    if (this.excludedDevices) this.excludedDevices.push(serial);
  }
  removeFromExcludedDevices(serial: string) {
    if (!this.excludedDevices || isEmpty(this.excludedDevices)) return;
    // We have non empty excluded devices
    const idx = this.excludedDevices.indexOf(serial);
    if (idx > -1) this.excludedDevices.splice(idx, 1);
  }

  isQueryActive() {
    return Array.isArray(this.selectionParams)
  }

  /**
   * The context param will be either context of the user
   * For example: If the user is in the group devices view, the context param will be groupId
   * If the user is in category devices view, the context param will be categoryLabel
   * @param contextParam
   * @returns
   */
  decorateQuery(contextParam: any){
    if (typeof this.selectionParams === 'undefined') return

    if (this.selectionParams.length > 0)  // Will AND the context to existing query
      this.selectionParams.splice(0, 0, {and: 'and'})
    this.selectionParams.splice(0, 0, contextParam)
  }

  generateQuery() {
    // If we have selectedFilters as undefined, we are in regular selection mode not selectAll
    if (typeof this.selectionParams === 'undefined') return undefined;

    // no selectionParams and no excluded devices == 0
    if (this.selectionParams.length == 0 && this.excludedDevices?.length == 0)
      return '';

    const queryParams: any[] = [...this.selectionParams];

    if (queryParams.length) {
      // We have filters applied
      if (this.excludedDevices?.length) {
        queryParams.push({ 'and not': 'and not' });
        queryParams.push({ serial: this.excludedDevices });
      }
    } else {
      // This means we have no selectionParams and only excludedDevices
      queryParams.push({ not: 'not' });
      queryParams.push({ serial: this.excludedDevices });
    }
    const queryString = convertSearchObjsToQueryString(queryParams);
    return queryString;
  }
}

/**
 * This will track the ViewModel and will be used by the View
 * It will have additional properties:
 * - count: number // total count of contextual devices
 * - selectAll: boolean // if selectAll has to be set
 * - selectedDevices: string[] // selected devices serial number
 */
class DevicesSelectionViewModel extends DeviceSelectionModel {
  count: number // total count of contextual devices
  selectAll: boolean // if selectAll has to be set
  selectedDevices: string[] // selected devices serial number

  constructor(
    count?: number,
    selectionParams?: any[],
    selectAll?: boolean,
    selectedDevices?: string[]
  ) {
    super(selectionParams);
    this.count = count || 0;
    this.selectAll = selectAll || false;
    this.selectedDevices = selectedDevices || [];
  }

  addDeviceToSelection(serial: string) {
    const idx = this.selectedDevices.indexOf(serial);
    if (idx > -1) return;
    this.removeFromExcludedDevices(serial);
    this.selectedDevices.push(serial);
    this.count++;
    if (this.excludedDevices && this.excludedDevices.length == 0)
      this.selectAll = true;
  }

  removeDeviceFromSelection(serial: string) {
    this.addToExcludedDevices(serial);
    // let us remove from selected devices
    const idx = this.selectedDevices.indexOf(serial);
    if (idx > -1) {
      this.selectedDevices.splice(idx, 1);
      this.count--;
      if (this.excludedDevices) {
        // We have to set the value of selectAll
        // If we still have a few in excluded devices list, we will set it as false else to true
        if (this.excludedDevices.length) this.selectAll = false;
        else this.selectAll = true;
      }
    }
  }

  makeACopy() {
    const copy = new DevicesSelectionViewModel();
    copy.count = this.count;
    copy.selectAll = this.selectAll;
    copy.selectedDevices = this.selectedDevices;
    if (typeof this.selectionParams !== 'undefined')
      copy.selectionParams = [...this.selectionParams]
    copy.excludedDevices = this.excludedDevices
    return copy;
  }

  public static initEmptyDevicesSelection() {
    return new DevicesSelectionViewModel(0, undefined, false, []);
  }

  public static initSelectAll(
    selectionParams: any[],
    count: number,
    devices: string[]
  ) {
    if (typeof selectionParams === 'undefined')
      throw new Error('initSelectAll needs defined selectionParam');
    // the count can be 0 , devices count can be 0 , devices list can be empty []
    // however the count cannt be less that the devices list
    if (count < devices.length)
      throw new Error(
        'initSelectAll - count cannt be less that count of passed in devices'
      );
    return new DevicesSelectionViewModel(count, selectionParams, true, devices);
  }
}

export { DevicesSelectionViewModel, DeviceSelectionModel };
